<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ResourceControlsAPI from 'dashboard/api/resourceControls';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ResourceInboxScope from './ResourceInboxScope.vue';

const { t, locale } = useI18n();
const router = useRouter();
const store = useStore();
const { accountId, currentAccount, accountScopedUrl } = useAccount();
const loading = ref(true);
const saving = ref('');
const error = ref('');
const inboxes = ref([]);
const items = computed(
  () => currentAccount.value.resource_controls?.items || []
);

const applySnapshot = resourceControls => {
  store.dispatch('accounts/applyResourceControls', {
    account_id: accountId.value,
    resource_controls: resourceControls,
  });
};

const showError = exception => {
  const code = exception.response?.data?.code;
  if (code === 'RESOURCE_UNAVAILABLE') {
    return t('ACCOUNT_RESOURCES.ERRORS.UNAVAILABLE');
  }
  if (code === 'INVALID_INBOX_SCOPE') {
    return t('ACCOUNT_RESOURCES.ERRORS.INVALID_INBOX_SCOPE');
  }
  return t(
    exception.response?.status === 403
      ? 'ACCOUNT_RESOURCES.ERRORS.FORBIDDEN'
      : 'ACCOUNT_RESOURCES.ERRORS.REQUEST_FAILED'
  );
};

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    await store.dispatch('accounts/get', {
      silent: true,
      accountId: accountId.value,
    });
    const { data } = await ResourceControlsAPI.get();
    inboxes.value = data.available_inboxes;
    applySnapshot(data);
  } catch (exception) {
    error.value = showError(exception);
  } finally {
    loading.value = false;
  }
};

const update = async (key, enabled) => {
  saving.value = key;
  try {
    const { data } = await ResourceControlsAPI.update(key, enabled);
    applySnapshot(data);
    useAlert(t('ACCOUNT_RESOURCES.SAVED'));
  } catch (exception) {
    useAlert(showError(exception));
  } finally {
    saving.value = '';
  }
};

const updateInboxes = async (key, inboxIds) => {
  saving.value = key;
  try {
    const { data } = await ResourceControlsAPI.updateInboxes(key, inboxIds);
    applySnapshot(data);
    useAlert(t('ACCOUNT_RESOURCES.SAVED'));
  } catch (exception) {
    useAlert(showError(exception));
  } finally {
    saving.value = '';
  }
};

const changedAt = change =>
  t('ACCOUNT_RESOURCES.LAST_CHANGE', {
    name: change.name,
    date: new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(change.at)),
  });

onMounted(load);
</script>

<template>
  <main class="h-full w-full overflow-y-auto bg-n-background">
    <div class="mx-auto max-w-4xl px-3 py-5 sm:px-6 sm:py-8">
      <header class="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p class="m-0 mb-2 text-sm font-medium text-n-brand">
            {{ t('ACCOUNT_RESOURCES.MASTER') }}
          </p>
          <h1 class="m-0 text-2xl font-semibold text-n-slate-12">
            {{ t('ACCOUNT_RESOURCES.TITLE') }}
          </h1>
          <p class="m-0 mt-2 text-sm text-n-slate-11">
            {{ currentAccount.name }}
          </p>
        </div>
        <Button
          ghost
          icon="i-lucide-arrow-left"
          :label="t('ACCOUNT_RESOURCES.BACK')"
          @click="router.push(accountScopedUrl('dashboard'))"
        />
      </header>

      <p
        class="mb-6 rounded-lg border border-n-weak bg-n-surface-2 p-4 text-sm text-n-slate-12"
      >
        {{ t('ACCOUNT_RESOURCES.DESCRIPTION') }}
      </p>
      <div v-if="loading" class="flex justify-center p-8" role="status">
        <Spinner />
        <span class="sr-only">{{ t('ACCOUNT_RESOURCES.LOADING') }}</span>
      </div>
      <div v-else-if="error" role="alert" class="text-sm text-n-ruby-11">
        <p>{{ error }}</p>
        <Button :label="t('ACCOUNT_RESOURCES.RETRY')" @click="load" />
      </div>
      <div v-else class="flex flex-col gap-2">
        <section
          v-for="item in items"
          :key="item.key"
          :aria-labelledby="`resource-${item.key}`"
          class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-4 py-3"
        >
          <div class="flex min-w-0 items-start justify-between gap-3">
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2
                  :id="`resource-${item.key}`"
                  class="m-0 text-base font-medium text-n-slate-12"
                >
                  {{
                    t(
                      `ACCOUNT_RESOURCES.FEATURES.${item.key.toUpperCase()}.NAME`
                    )
                  }}
                </h2>
                <p
                  class="m-0 rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
                >
                  {{
                    t(
                      !item.available
                        ? 'ACCOUNT_RESOURCES.UNAVAILABLE'
                        : 'ACCOUNT_RESOURCES.AVAILABLE'
                    )
                  }}
                </p>
              </div>
              <p class="m-0 mt-1 text-xs text-n-slate-11">
                {{
                  t(
                    item.enabled
                      ? 'ACCOUNT_RESOURCES.ENABLED'
                      : 'ACCOUNT_RESOURCES.DISABLED'
                  )
                }}
              </p>
              <p class="m-0 mt-1 text-sm text-n-slate-11">
                {{
                  t(
                    `ACCOUNT_RESOURCES.FEATURES.${item.key.toUpperCase()}.DESCRIPTION`
                  )
                }}
              </p>
              <p
                v-if="item.last_change"
                class="m-0 mt-1 text-xs text-n-slate-10"
              >
                {{ changedAt(item.last_change) }}
              </p>
            </div>
            <Switch
              :model-value="item.enabled && item.available"
              :disabled="Boolean(saving) || !item.available"
              :aria-labelledby="`resource-${item.key}`"
              class="shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
              @update:model-value="value => update(item.key, value)"
            />
          </div>
          <ResourceInboxScope
            v-if="item.inbox_scoped"
            :resource-key="item.key"
            :selected-ids="currentAccount.resource_controls.inbox_ids[item.key]"
            :inboxes="inboxes"
            :busy="saving === item.key"
            :disabled="Boolean(saving) || !item.available"
            @save="ids => updateInboxes(item.key, ids)"
          />
        </section>
        <p
          role="status"
          aria-live="polite"
          class="m-0 min-h-5 text-sm text-n-slate-11"
        >
          {{ saving ? t('ACCOUNT_RESOURCES.SAVING') : '' }}
        </p>
      </div>
    </div>
  </main>
</template>
